/**
 * Admin / Super Admin "Invoices" tab — The Indian Pest Control branded.
 * Routes used: /api/panel/admin/invoices/*
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Receipt, Plus, Search, Download, Printer, Send, Mail, Trash2, Copy, Edit2, X,
  Loader2, FileText, IndianRupee, CheckCircle2, Clock, AlertCircle, XCircle,
  RefreshCw, Eye, FileSpreadsheet, TrendingUp, ChevronDown, BarChart3, MessageCircle,
  Link2, Sparkles, ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DatePickerPro, DateRangePicker } from '../../components/ui/date-picker-pro';
import BookingsTimeFilter from './BookingsTimeFilter';
import { useDeleteConfirm } from '../../components/DeleteConfirmDialog';
import WhatsAppSendDialog from '../../components/WhatsAppSendDialog';

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Safely turn any API error (incl. FastAPI 422 detail arrays/objects) into a readable string.
const apiErr = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).slice(-1)[0] || 'field'}: ${x.msg || ''}`).join('; ');
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  return fallback || e?.message || 'Something went wrong';
};

const makeRowId = () => `invoice-row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const makeItem = () => ({ _row_id: makeRowId(), description: '', quantity: 1, rate: 0, pest_names: [], pest_other: '', segments: ['residential'], property_size: '', area_sqft: '', warranty_value: '', warranty_unit: 'months' });
const withRowIds = (items) => (Array.isArray(items) ? items : []).map((item) => ({ ...makeItem(), ...(item || {}) }));
const stripRowIds = (items) => (Array.isArray(items) ? items : []).map(({ _row_id, ...item }) => item);
const comparableForm = (value) => ({ ...value, items: stripRowIds(value?.items) });

class InvoiceErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() { return { hasError: true }; }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="p-5 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg" role="alert">
        Something went wrong while displaying this invoice. Please close it and try again.
        <button type="button" className="ml-2 underline font-semibold" onClick={() => this.setState({ hasError: false })}>Try again</button>
      </div>
    );
  }
}

const STATUSES = [
  { v: '', label: 'All statuses' },
  { v: 'paid', label: 'Paid' },
  { v: 'partial', label: 'Partial' },
  { v: 'pending', label: 'Pending' },
  { v: 'overdue', label: 'Overdue' },
  { v: 'cancelled', label: 'Cancelled' },
];

const STATUS_META = {
  paid: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  partial: { cls: 'bg-blue-100 text-blue-700 border-blue-200', Icon: IndianRupee },
  pending: { cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: Clock },
  overdue: { cls: 'bg-rose-100 text-rose-700 border-rose-200', Icon: AlertCircle },
  cancelled: { cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: XCircle },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.Icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${m.cls}`}><Icon size={10} /> {(status || 'pending').toUpperCase()}</span>;
}

const CATEGORY_TABS = [
  { id: 'all', label: 'All Invoices', desc: 'Combined GST + Non-GST', color: 'slate', apiType: '' },
  { id: 'gst', label: 'GST Invoices', desc: 'Tax invoices only', color: 'emerald', apiType: 'gst' },
  { id: 'nongst', label: 'Non-GST Invoices', desc: 'Estimates / Bill of Supply', color: 'amber', apiType: 'nongst' },
];

const CATEGORY_COLORS = {
  all: { active: 'border-slate-700 text-slate-900 bg-slate-50', idle: 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50/50' },
  gst: { active: 'border-emerald-600 text-emerald-800 bg-emerald-50', idle: 'border-transparent text-slate-500 hover:text-emerald-700 hover:bg-emerald-50/40' },
  nongst: { active: 'border-amber-600 text-amber-800 bg-amber-50', idle: 'border-transparent text-slate-500 hover:text-amber-700 hover:bg-amber-50/40' },
};

export default function InvoicesAdminTab({ api, isSuper }) {
  const [category, setCategory] = useState('all'); // all | gst | nongst
  const [subTab, setSubTab] = useState('list'); // list | reports
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: '', date_from: '', date_to: '' });
  const [editing, setEditing] = useState(null);
  const [active, setActive] = useState(null);
  const [waSend, setWaSend] = useState(null);
  const [waEnabled, setWaEnabled] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [autoSendInvoice, setAutoSendInvoice] = useState(false);

  const apiType = CATEGORY_TABS.find(c => c.id === category)?.apiType || '';

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const p = new URLSearchParams();
      p.set('page', page);
      p.set('page_size', pageSize);
      if (apiType) p.set('invoice_type', apiType);
      if (filters.search) p.set('search', filters.search);
      if (filters.status) p.set('status', filters.status);
      if (filters.date_from) p.set('date_from', filters.date_from);
      if (filters.date_to) p.set('date_to', filters.date_to);
      const sumP = apiType ? `?invoice_type=${apiType}` : '';
      const [r, s] = await Promise.all([
        api('GET', `/admin/invoices?${p.toString()}`),
        api('GET', `/admin/invoices/summary${sumP}`),
      ]);
      setRows(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setSummary(s.data || null);
    } catch (e) { setErr(apiErr(e)); }
    finally { setLoading(false); }
  }, [api, page, pageSize, filters, apiType]);

  useEffect(() => { load(); }, [load]);

  // Reset page when category changes
  useEffect(() => { setPage(1); }, [category]);

  useEffect(() => {
    api('GET', '/admin/whatsapp/config')
      .then((r) => setWaEnabled(!!r.data?.send_buttons_enabled && (r.data?.accounts || []).some((a) => a.enabled && a.has_key)))
      .catch(() => setWaEnabled(false));
    // Load invoice auto-send setting
    api('GET', '/admin/invoices/settings')
      .then((r) => setAutoSendInvoice(!!r.data?.auto_send_invoice))
      .catch(() => {});
  }, [api]);

  const toggleAutoSend = async () => {
    const newVal = !autoSendInvoice;
    try {
      await api('PATCH', '/admin/invoices/settings', { auto_send_invoice: newVal });
      setAutoSendInvoice(newVal);
    } catch (e) { alert(apiErr(e, 'Failed to update setting')); }
  };

  const handleTimeFilterChange = useCallback((v) => {
    setFilters((f) => {
      const nextFrom = v.from || '';
      const nextTo = v.to || '';
      if (f.date_from === nextFrom && f.date_to === nextTo) return f;
      return { ...f, date_from: nextFrom, date_to: nextTo };
    });
  }, []);

  const timeFilterValue = useMemo(() => ({
    scope: filters.date_from || filters.date_to ? 'custom' : 'all',
    from: filters.date_from || null,
    to: filters.date_to || null,
    slotKey: 'any',
    search: '',
  }), [filters.date_from, filters.date_to]);

  useEffect(() => {
    const openWith = (bookingId) => {
      if (!bookingId) return;
      setEditing({ __prefill_booking_id: bookingId });
    };
    const handler = (e) => openWith(e.detail?.bookingId);
    window.addEventListener('entorid:open-invoice-creator', handler);
    if (window.__entoridPendingInvoicePrefill) {
      openWith(window.__entoridPendingInvoicePrefill);
      delete window.__entoridPendingInvoicePrefill;
    }
    return () => window.removeEventListener('entorid:open-invoice-creator', handler);
  }, []);

  const downloadPDF = async (inv) => {
    try {
      const r = await api('GET', `/admin/invoices/${inv.id}/pdf`, null, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TheIndianPestControl_Invoice_${inv.invoice_number.replace(/.*-/, '')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(apiErr(e)); }
  };
  const printPDF = async (inv) => {
    try {
      const r = await api('GET', `/admin/invoices/${inv.id}/pdf`, null, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const w = window.open(url, '_blank');
      if (w) w.addEventListener('load', () => { try { w.print(); } catch { /* */ } });
    } catch (e) { alert(apiErr(e)); }
  };
  const sendEmail = async (inv) => {
    if (!inv.customer_email) return alert('Customer has no email on file');
    if (!window.confirm(`Email invoice ${inv.invoice_number} to ${inv.customer_email}?`)) return;
    setBusy(true);
    try { await api('POST', `/admin/invoices/${inv.id}/send-email`); alert('Email sent successfully!'); await load(); }
    catch (e) { alert(apiErr(e)); }
    finally { setBusy(false); }
  };
  const sendWA = async (inv) => {
    try {
      const r = await api('GET', `/admin/invoices/${inv.id}/wa-url`);
      if (r.data?.url) window.open(r.data.url, '_blank');
    } catch (e) { alert(apiErr(e)); }
  };
  const duplicate = async (inv) => {
    setBusy(true);
    try { await api('POST', `/admin/invoices/${inv.id}/duplicate`); await load(); }
    catch (e) { alert(apiErr(e)); }
    finally { setBusy(false); }
  };
  const { confirm: confirmDelete } = useDeleteConfirm();
  const remove = async (inv) => {
    const ok = await confirmDelete({
      module: 'invoices',
      id: inv.id,
      name: `Invoice ${inv.invoice_number}`,
    });
    if (ok) load();
  };
  const exportCSV = () => {
    const typeParam = apiType ? `&invoice_type=${apiType}` : '';
    api('GET', `/admin/invoices/export.csv?_=${Date.now()}${typeParam}`, null, { responseType: 'blob' }).then(r => {
      const typeLabel = { gst: 'GST', nongst: 'NonGST' }[category] || 'All';
      const blob = new Blob([r.data], { type: 'text/csv' });
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = u; a.download = `TIPC_${typeLabel}_Invoices_Export.csv`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    }).catch(e => alert(apiErr(e)));
  };

  const catMeta = CATEGORY_TABS.find(c => c.id === category) || CATEGORY_TABS[0];

  return (
    <div data-testid="invoices-admin-tab">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2"><Receipt size={20} className="text-emerald-700" /> Invoices</h1>
          <p className="text-sm text-slate-500">The Indian Pest Control · GST-compliant invoicing, reminders, reports.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={load} data-testid="inv-refresh"><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="inv-export-csv"><FileSpreadsheet size={14} className="mr-1.5" /> {catMeta.label} CSV</Button>
          <Button onClick={() => setEditing('new')} className="bg-emerald-700 hover:bg-emerald-800" size="sm" data-testid="inv-new"><Plus size={14} className="mr-1" /> New Invoice</Button>
        </div>
      </div>

      {/* ===== Auto-Send Toggle ===== */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-3 mb-4" data-testid="inv-auto-send-row">
        <div className="flex items-center gap-2.5">
          <Send size={15} className="text-emerald-600" />
          <div>
            <div className="text-sm font-semibold text-slate-800">Auto-Send Invoice to Customer</div>
            <div className="text-[11px] text-slate-500">When ON, newly created invoices are automatically emailed to customers</div>
          </div>
        </div>
        <button
          onClick={toggleAutoSend}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${autoSendInvoice ? 'bg-emerald-600' : 'bg-slate-300'}`}
          data-testid="inv-auto-send-toggle"
          aria-label="Toggle auto-send"
        >
          <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transform transition-transform ${autoSendInvoice ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>

      {/* ===== Category Tabs (All / GST / Non-GST) ===== */}
      <div className="flex gap-1.5 mb-4" data-testid="inv-category-tabs">
        {CATEGORY_TABS.map(ct => {
          const isOn = category === ct.id;
          const colors = CATEGORY_COLORS[ct.id];
          return (
            <button
              key={ct.id}
              onClick={() => { setCategory(ct.id); setSubTab('list'); }}
              className={`px-4 py-2.5 rounded-t-xl text-sm font-semibold border-b-[3px] transition-all ${isOn ? colors.active : colors.idle}`}
              data-testid={`inv-cat-${ct.id}`}
            >
              <span>{ct.label}</span>
              {summary && isOn && <span className="ml-2 text-xs opacity-70">({summary.total_invoices})</span>}
            </button>
          );
        })}
      </div>

      {/* Sub-tabs: List / Reports within the selected category */}
      <div className="flex gap-1 mb-4 border-b border-slate-200">
        <TabBtn id="list" active={subTab} onClick={setSubTab} Icon={Receipt} label="List" />
        <TabBtn id="reports" active={subTab} onClick={setSubTab} Icon={BarChart3} label="Reports" />
      </div>

      {/* Summary widgets — scoped to current category */}
      {summary && subTab === 'list' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-5">
          <Widget label="Total" value={summary.total_invoices} Icon={FileText} tone="slate" />
          <Widget label="Paid" value={summary.paid_count} Icon={CheckCircle2} tone="emerald" />
          <Widget label="Pending" value={summary.pending_count} Icon={Clock} tone="amber" />
          <Widget label="Overdue" value={summary.overdue_count} Icon={AlertCircle} tone="rose" />
          <Widget label="Revenue" value={fmtMoney(summary.revenue_collected)} Icon={IndianRupee} tone="blue" />
          {category === 'gst' ? (
            <Widget label="GST Collected" value={fmtMoney(summary.gst_collected || 0)} Icon={TrendingUp} tone="violet" />
          ) : (
            <Widget label="Outstanding" value={fmtMoney(summary.outstanding)} Icon={TrendingUp} tone="rose" />
          )}
        </div>
      )}

      {subTab === 'list' && (
        <>
          {/* Filters */}
          <div className="bg-white border border-slate-200 rounded-xl p-3 mb-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Invoice #, customer, service…" value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} className="pl-9 h-10" data-testid="inv-filter-search" />
              </div>
              <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))} className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="inv-filter-status">
                {STATUSES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
              <BookingsTimeFilter
                value={timeFilterValue}
                onChange={handleTimeFilterChange}
                hideSlot
                label="Date"
                searchPlaceholder="(use the search box on the left)"
              />
            </div>
          </div>

          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 mb-3">{err}</div>}

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[850px]">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2.5">Invoice #</th>
                  {category === 'all' && <th className="text-center px-2 py-2.5">Type</th>}
                  <th className="text-left px-3 py-2.5">Customer</th>
                  <th className="text-left px-3 py-2.5">Service</th>
                  <th className="text-left px-3 py-2.5">Date</th>
                  <th className="text-right px-3 py-2.5">Total</th>
                  <th className="text-right px-3 py-2.5">Balance</th>
                  <th className="text-center px-3 py-2.5">Status</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (<tr><td colSpan={category === 'all' ? 9 : 8} className="py-10 text-center text-slate-400"><Loader2 className="inline animate-spin" size={16} /> Loading…</td></tr>)}
                {!loading && rows.length === 0 && (<tr><td colSpan={category === 'all' ? 9 : 8} className="py-12 text-center text-slate-400">No invoices match these filters.</td></tr>)}
                {!loading && rows.map(r => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`inv-row-${r.id}`}>
                    <td className="px-3 py-2 font-semibold text-slate-900">{r.invoice_number}</td>
                    {category === 'all' && (
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${r.is_gst !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {r.is_gst !== false ? 'GST' : 'EST'}
                        </span>
                      </td>
                    )}
                    <td className="px-3 py-2"><div className="text-slate-900 font-medium">{r.customer_name}</div><div className="text-[11px] text-slate-500">{r.customer_phone}</div></td>
                    <td className="px-3 py-2 text-slate-700 max-w-[180px] truncate">{r.service_name || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{fmtDate(r.invoice_date)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtMoney(r.total_amount)}</td>
                    <td className="px-3 py-2 text-right text-rose-700 font-semibold">{fmtMoney(r.amount_due)}</td>
                    <td className="px-3 py-2 text-center"><StatusBadge status={r.payment_status} /></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-0.5 flex-wrap">
                        <IconBtn title="View / Pay" onClick={() => setActive(r)} Icon={Eye} testid={`inv-act-view-${r.id}`} />
                        <IconBtn title="Edit" onClick={() => setEditing(r)} Icon={Edit2} testid={`inv-act-edit-${r.id}`} />
                        <IconBtn title="Download PDF" onClick={() => downloadPDF(r)} Icon={Download} testid={`inv-act-dl-${r.id}`} />
                        <IconBtn title="Print" onClick={() => printPDF(r)} Icon={Printer} testid={`inv-act-pr-${r.id}`} />
                        <IconBtn title="Email" onClick={() => sendEmail(r)} Icon={Mail} testid={`inv-act-em-${r.id}`} disabled={!r.customer_email || busy} />
                        {r.sent_to_customer && <span className="text-[8px] font-bold text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded" title={`Sent ${r.sent_at ? new Date(r.sent_at).toLocaleString('en-IN') : ''}`}>SENT</span>}
                        <IconBtn title="WhatsApp (share link)" onClick={() => sendWA(r)} Icon={MessageCircle} testid={`inv-act-wa-${r.id}`} />
                        {waEnabled && <IconBtn title="Send on WhatsApp (AiSensy)" onClick={() => setWaSend(r)} Icon={Send} testid={`inv-act-wasend-${r.id}`} tone="emerald" />}
                        <IconBtn title="Duplicate" onClick={() => duplicate(r)} Icon={Copy} testid={`inv-act-dup-${r.id}`} disabled={busy} />
                        {isSuper && <IconBtn title="Delete" onClick={() => remove(r)} Icon={Trash2} testid={`inv-act-del-${r.id}`} disabled={busy} tone="rose" />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center justify-between mt-3 text-sm">
              <div className="text-slate-500">Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </>
      )}

      {subTab === 'reports' && <ReportsPanel api={api} invoiceType={apiType} categoryLabel={catMeta.label} />}

      {editing && <InvoiceErrorBoundary><InvoiceFormModal
        api={api}
        isSuper={isSuper}
        initial={(editing === 'new' || editing?.__prefill_booking_id) ? null : editing}
        prefillBookingId={editing?.__prefill_booking_id || null}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      /></InvoiceErrorBoundary>}
      {active && <InvoiceActionModal api={api} invoice={active} onClose={() => setActive(null)} onEdit={() => { setActive(null); setEditing(active); }} onChanged={() => { setActive(null); load(); }} onDownload={() => downloadPDF(active)} onPrint={() => printPDF(active)} onEmail={() => sendEmail(active)} onWA={() => sendWA(active)} />}
      {waSend && <WhatsAppSendDialog api={api} docType="invoice" docId={waSend.id} docLabel={`Invoice ${waSend.invoice_number}`} defaultPhone={waSend.customer_whatsapp || waSend.customer_phone} onClose={() => setWaSend(null)} />}
    </div>
  );
}

function TabBtn({ id, active, onClick, Icon, label }) {
  const isOn = active === id;
  return (
    <button onClick={() => onClick(id)} className={`px-4 py-2 text-sm font-semibold border-b-2 inline-flex items-center gap-1.5 ${isOn ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid={`inv-subtab-${id}`}>
      <Icon size={14} /> {label}
    </button>
  );
}

function IconBtn({ Icon, title, onClick, testid, disabled, tone = 'slate' }) {
  const tones = {
    slate: 'text-slate-600 hover:bg-slate-100',
    rose: 'text-rose-600 hover:bg-rose-50',
    emerald: 'text-emerald-600 hover:bg-emerald-50',
  };
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled} data-testid={testid} className={`w-7 h-7 rounded-md inline-flex items-center justify-center disabled:opacity-40 ${tones[tone]}`}>
      <Icon size={13} />
    </button>
  );
}

function Widget({ label, value, Icon, tone }) {
  const tones = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    rose: 'bg-rose-50 text-rose-800 border-rose-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
  };
  return (
    <div className={`border rounded-xl p-3 ${tones[tone]}`} data-testid={`inv-widget-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider font-bold opacity-75">{label}</span>
        <Icon size={13} className="opacity-60" />
      </div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

// ============================================================
// CREATE / EDIT MODAL
// ============================================================
function InvoiceFormModal({ api, isSuper, initial, prefillBookingId, onClose, onSaved }) {
  const isNew = !initial;
  const defaultForm = useMemo(() => ({
    customer_name: '', customer_phone: '', customer_email: '', customer_address: '',
    customer_whatsapp: '', customer_company_name: '', customer_gst_number: '',
    service_name: '', service_type: '', property_type: '', area: '', unit: '',
    service_date: '', service_start_date: '', service_end_date: '',
    booking_id: '', booking_code: '', invoice_number: '', invoice_date: '',
    is_gst: true,
    pest_types: [], pest_other_desc: '',
    dispatched_through: [],
    items: [makeItem()],
    discount: 0, gst_rate: 18, gst_inclusive: false, due_in_days: 7,
    amount_paid: 0, payment_status: 'pending', payment_mode: 'cash', payment_method: '', payment_transaction_id: '', payment_date: '',
    notes: '', terms: '',
  }), []);
  const [form, setForm] = useState(() => {
    const base = initial ? { ...defaultForm, ...initial, payment_method: initial.payment_mode || '' } : defaultForm;
    return { ...base, items: withRowIds(Array.isArray(base.items) && base.items.length ? base.items : [makeItem()]) };
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [pestList, setPestList] = useState([]);
  const [autoPest, setAutoPest] = useState({ pest_slug: '', property_size: '', area_sqft: '', drilling_points: '', emergency: false, travel_km: '' });
  const [showLookup, setShowLookup] = useState(false);
  const [expandedRow, setExpandedRow] = useState(0); // first row open by default
  const [hasWarned, setHasWarned] = useState(false);
  const initialState = useMemo(() => JSON.stringify(comparableForm(initial ? {
    ...defaultForm,
    ...initial,
    payment_method: initial.payment_mode || '',
  } : defaultForm)), [initial, defaultForm]);
  const isDirty = useMemo(() => JSON.stringify(comparableForm(form)) !== initialState, [form, initialState]);
  useEffect(() => {
    const handler = (e) => {
      if (!isNew && isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, isNew]);

  const DISPATCH_OPTIONS = ['WhatsApp', 'Email', 'Hard Copy', 'Courier', 'In-Person'];

  const togglePest = (name) => {
    setForm(f => {
      const set = new Set(f.pest_types || []);
      if (set.has(name)) set.delete(name); else set.add(name);
      return { ...f, pest_types: Array.from(set) };
    });
  };
  const toggleDispatch = (name) => {
    setForm(f => {
      const set = new Set(f.dispatched_through || []);
      if (set.has(name)) set.delete(name); else set.add(name);
      return { ...f, dispatched_through: Array.from(set) };
    });
  };

  // Load pest catalog for auto-fill — once on mount; `api` closes over current token in context
  useEffect(() => {
    api('GET', '/admin/pest-catalog').then(r => setPestList(r.data?.items || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-prefill when the modal is opened from Force-Complete with a pending booking id
  useEffect(() => {
    if (prefillBookingId) {
      loadBookingById(prefillBookingId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillBookingId]);

  const runAutoFill = async () => {
    if (!autoPest.pest_slug) { setErr('Pick a pest type first'); return; }
    setErr('');
    try {
      const payload = { pest_slug: autoPest.pest_slug };
      if (autoPest.property_size) payload.property_size = autoPest.property_size;
      if (autoPest.area_sqft) payload.area_sqft = Number(autoPest.area_sqft);
      if (autoPest.drilling_points) payload.drilling_points = Number(autoPest.drilling_points);
      if (autoPest.travel_km) payload.travel_km = Number(autoPest.travel_km);
      if (autoPest.emergency) payload.emergency = true;
      const r = await api('POST', '/admin/pricing/quote', payload);
      const q = r.data || {};
      setForm(f => ({
        ...f,
        service_name: q.service_name || f.service_name,
        items: q.items?.length ? withRowIds(q.items) : f.items,
        gst_rate: q.gst_rate ?? f.gst_rate,
        // Auto-mirror selected pest into pest_types (multi-select) so the chip lights up
        pest_types: (() => {
          const cur = new Set(f.pest_types || []);
          if (q.service_name) cur.add(q.service_name);
          return Array.from(cur);
        })(),
      }));
    } catch (e) { setErr(apiErr(e)); }
  };

  const setItem = (i, field, val) => {
    setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [field]: val } : it) }));
  };
  const toggleItemSegment = (i, seg) => {
    setForm(f => ({
      ...f,
      items: f.items.map((it, idx) => {
        if (idx !== i) return it;
        const set = new Set(it.segments || []);
        if (set.has(seg)) set.delete(seg); else set.add(seg);
        return { ...it, segments: Array.from(set) };
      }),
    }));
  };
  const toggleItemPest = (i, pestName) => {
    setForm(f => ({
      ...f,
      items: f.items.map((it, idx) => {
        if (idx !== i) return it;
        const set = new Set(it.pest_names || []);
        if (set.has(pestName)) set.delete(pestName); else set.add(pestName);
        return { ...it, pest_names: Array.from(set) };
      }),
    }));
  };
  const addItem = () => setForm(f => ({
    ...f,
    items: [...(f.items || []), makeItem()],
  }));
  const delItem = (i) => setForm(f => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, idx) => idx !== i) : f.items }));

  // ---- Booking ID prefill — when admin enters a booking code/id or picks from search ----
  const applyPrefill = (data) => {
    setForm(f => ({
      ...f,
      booking_id: data.booking_id || data.id || '',
      booking_code: data.booking_code || '',
      customer_id: data.customer_id || null,
      customer_name: data.customer_name || f.customer_name,
      customer_phone: data.customer_phone || f.customer_phone,
      customer_whatsapp: data.customer_whatsapp || data.customer_phone || f.customer_whatsapp,
      customer_email: data.customer_email || f.customer_email,
      customer_address: data.customer_address || f.customer_address,
      service_name: data.service_name || f.service_name,
      service_date: data.service_date || f.service_date,
      service_start_date: data.service_start_date || f.service_start_date,
      service_end_date: data.service_end_date || f.service_end_date,
      pest_types: data.pest_types?.length ? data.pest_types : f.pest_types,
      items: Array.isArray(data.items) && data.items.length ? withRowIds(data.items.map(it => ({
        description: it.description || '', quantity: it.quantity || 1, rate: it.rate || 0,
        pest_names: it.pest_names || [], pest_other: it.pest_other || '',
        segments: it.segments || ['residential'],
        property_size: it.property_size || '', area_sqft: it.area_sqft || '',
        warranty_value: it.warranty_value || '', warranty_unit: it.warranty_unit || 'months',
      }))) : f.items,
    }));
  };
  const loadBookingById = async (bookingId) => {
    if (!bookingId) return;
    setErr('');
    try {
      const r = await api('GET', `/admin/invoices/prefill-from-booking/${encodeURIComponent(bookingId)}`);
      applyPrefill(r.data || {});
    } catch (e) { setErr(apiErr(e) || 'Booking not found'); }
  };

  const totals = useMemo(() => {
    const sub = (form.items || []).reduce((a, it) => a + (Number(it.quantity || 0) * Number(it.rate || 0)), 0);
    const disc = Number(form.discount || 0);
    const taxableBeforeGst = Math.max(0, sub - disc);
    const effectiveRate = form.is_gst ? Number(form.gst_rate || 0) : 0;
    const inclusive = Boolean(form.is_gst && form.gst_inclusive);
    const taxable = inclusive && effectiveRate > 0 ? taxableBeforeGst / (1 + effectiveRate / 100) : taxableBeforeGst;
    const gst = inclusive ? taxableBeforeGst - taxable : taxable * effectiveRate / 100;
    const total = inclusive ? taxableBeforeGst : taxable + gst;
    const paid = Number(form.amount_paid || 0);
    const balance = Math.max(0, total - paid);
    return { sub, disc, gst, total, effectiveRate, paid, balance, inclusive };
  }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name?.trim() || !form.customer_phone?.trim()) {
      setErr('Customer name and phone are required'); return;
    }
    const items = Array.isArray(form.items) ? form.items : [];
    if (!items.length) { setErr('At least one line item required'); return; }
    const invalidRow = items.findIndex((item) => {
      const quantity = Number(item?.quantity);
      const rate = Number(item?.rate);
      return !item || typeof item.description !== 'string' || !item.description.trim() || !Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(rate) || rate < 0;
    });
    if (invalidRow >= 0) { setErr(`Please complete the required fields in Service Row ${invalidRow + 1}.`); setExpandedRow(invalidRow); return; }
    setBusy(true); setErr('');
    const payload = {
      ...form,
      items: stripRowIds(items),
      payment_mode: form.payment_method || form.payment_mode,
    };
    try {
      if (isNew) await api('POST', '/admin/invoices', payload);
      else await api('PATCH', `/admin/invoices/${initial.id}`, payload);
      onSaved();
    } catch (e2) { setErr(apiErr(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="inv-form-modal">
      <form onSubmit={submit} className="bg-white w-full sm:max-w-3xl rounded-2xl overflow-hidden max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 bg-emerald-700 text-white">
          <div>
            <div className="text-xs opacity-90">The Indian Pest Control</div>
            <div className="font-bold">{isNew ? 'New Invoice' : `Edit ${initial.invoice_number}`}</div>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          {/* ---- Booking lookup banner (admin only — auto-fill customer + service from booking) ---- */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3" data-testid="inv-booking-lookup">
            <div className="flex items-center gap-2 mb-2">
              <Link2 size={14} className="text-indigo-700" />
              <span className="text-xs font-bold uppercase tracking-wide text-indigo-900">Find by Booking</span>
              <span className="text-[11px] text-indigo-700">(auto-fills name, mobile, email, address, service)</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={form.booking_code || form.booking_id || ''}
                onChange={(e) => setForm({ ...form, booking_code: e.target.value, booking_id: '' })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); loadBookingById(form.booking_code || form.booking_id); } }}
                placeholder="Booking ID or Code (e.g., BK260624002) — press Enter"
                className="h-10 text-sm flex-1"
                data-testid="inv-booking-id-input"
              />
              <Button type="button" variant="outline" onClick={() => loadBookingById(form.booking_code || form.booking_id)} className="border-indigo-300 text-indigo-700 hover:bg-indigo-100" data-testid="inv-booking-go">Apply</Button>
              <Button type="button" variant="outline" onClick={() => setShowLookup(true)} className="border-indigo-300 text-indigo-700 hover:bg-indigo-100" data-testid="inv-booking-search-open"><Search size={14} className="mr-1" /> Search</Button>
            </div>
            {form.booking_code && (
              <div className="mt-2 text-xs text-indigo-800">Linked: <b>{form.booking_code}</b> {form.customer_name && <>· {form.customer_name}</>}</div>
            )}
          </div>

          {/* ---- GST / Non-GST toggle ---- */}
          <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200 rounded-xl p-3" data-testid="inv-gst-toggle-row">
            <span className="text-sm font-semibold text-slate-700">Invoice Type:</span>
            <div className="inline-flex rounded-full border border-slate-300 bg-white p-0.5">
              <button type="button" onClick={() => setForm({ ...form, is_gst: true })} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${form.is_gst ? 'bg-emerald-600 text-white' : 'text-slate-600'}`} data-testid="inv-gst-on">GST Invoice (TIPC-GST-…)</button>
              <button type="button" onClick={() => setForm({ ...form, is_gst: false })} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${!form.is_gst ? 'bg-amber-600 text-white' : 'text-slate-600'}`} data-testid="inv-gst-off">Non-GST / Estimate (TIPC-EST-…)</button>
            </div>
            <span className="text-[11px] text-slate-500 ml-auto">{form.is_gst ? 'GST will be added on PDF' : 'No tax line; legally an Estimate'}</span>
          </div>

          <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Invoice Date</Label><DatePickerPro value={(form.invoice_date || '').slice(0, 10)} onChange={(v) => setForm({ ...form, invoice_date: v })} data-testid="inv-form-inv-date" /></div>
            <div><Label>Invoice Number</Label><Input value={form.invoice_number || ''} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} disabled={!isSuper && !!initial} data-testid="inv-form-inv-number" placeholder={isSuper ? 'Optional — override TIPC-INV number' : 'Editable by super admin only'} /></div>
            <div><Label>Customer Name *</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="inv-form-cust-name" /></div>
            <div><Label>Mobile *</Label><Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="inv-form-cust-phone" /></div>
            <div><Label>Company Name</Label><Input value={form.customer_company_name || ''} onChange={(e) => setForm({ ...form, customer_company_name: e.target.value })} data-testid="inv-form-cust-company" /></div>
            <div><Label>GST Number</Label><Input value={form.customer_gst_number || ''} onChange={(e) => setForm({ ...form, customer_gst_number: e.target.value })} data-testid="inv-form-cust-gst" /></div>
            <div><Label>Custom WhatsApp # <span className="text-slate-400 text-[11px]">(if different from mobile)</span></Label><Input value={form.customer_whatsapp || ''} onChange={(e) => setForm({ ...form, customer_whatsapp: e.target.value })} placeholder="e.g., +91 98765 43210" data-testid="inv-form-cust-wa" /></div>
            <div><Label>Email</Label><Input type="email" value={form.customer_email || ''} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="inv-form-cust-email" /></div>
            <div className="sm:col-span-2"><Label>Billing Address</Label><Input value={form.customer_address || ''} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} data-testid="inv-form-cust-addr" /></div>
            <div><Label>Service / Treatment</Label><Input value={form.service_name || ''} onChange={(e) => setForm({ ...form, service_name: e.target.value })} placeholder="e.g., Cockroach Control" data-testid="inv-form-service" /></div>
            <div><Label>Service Date</Label><DatePickerPro value={(form.service_date || '').slice(0, 10)} onChange={(v) => setForm({ ...form, service_date: v })} data-testid="inv-form-svc-date" /></div>
            <div><Label>Service Starting Date</Label><DatePickerPro value={(form.service_start_date || '').slice(0, 10)} onChange={(v) => setForm({ ...form, service_start_date: v })} data-testid="inv-form-svc-start" /></div>
            <div><Label>Service Ending Date</Label><DatePickerPro value={(form.service_end_date || '').slice(0, 10)} onChange={(v) => setForm({ ...form, service_end_date: v })} data-testid="inv-form-svc-end" /></div>
          </fieldset>

          {/* ---- Pest Types (multi-select with "Other" → description) ---- */}
          <fieldset className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <Label className="text-sm font-semibold mb-2 block">Pest Treatments (select all that apply)</Label>
            <div className="flex flex-wrap gap-2" data-testid="inv-form-pest-chips">
              {[...pestList.filter(p => p.active).map(p => p.name), 'Other / Not Listed'].map(name => {
                const active = (form.pest_types || []).includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => togglePest(name)}
                    data-testid={`inv-pest-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${active ? 'bg-emerald-600 text-white shadow' : 'bg-white border border-slate-300 text-slate-700 hover:border-emerald-400'}`}
                  >{name}</button>
                );
              })}
            </div>
            {(form.pest_types || []).includes('Other / Not Listed') && (
              <div className="mt-3" data-testid="inv-form-pest-other-wrap">
                <Label className="text-xs">Describe the pest issue *</Label>
                <textarea
                  rows={2}
                  value={form.pest_other_desc || ''}
                  onChange={(e) => setForm({ ...form, pest_other_desc: e.target.value })}
                  placeholder="e.g., Bird droppings on balcony, snake sighting, etc."
                  className="mt-1 w-full p-2 rounded-md border border-slate-300 text-sm"
                  data-testid="inv-form-pest-other-desc"
                />
              </div>
            )}
          </fieldset>

          {/* ---- Dispatched Through (multi-select) ---- */}
          <fieldset className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <Label className="text-sm font-semibold mb-2 block">Dispatched Through (select all used channels)</Label>
            <div className="flex flex-wrap gap-2" data-testid="inv-form-dispatch-chips">
              {DISPATCH_OPTIONS.map(name => {
                const active = (form.dispatched_through || []).includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => toggleDispatch(name)}
                    data-testid={`inv-dispatch-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${active ? 'bg-blue-600 text-white shadow' : 'bg-white border border-slate-300 text-slate-700 hover:border-blue-400'}`}
                  >{name}</button>
                );
              })}
            </div>
          </fieldset>

          {/* ---- Pest Pricing Auto-Fill ---- */}
          <details className="bg-emerald-50 rounded-lg p-3 border border-emerald-200" open={!initial}>
            <summary className="text-sm font-semibold text-emerald-900 cursor-pointer">🧮 Auto-fill from Pest Catalog Pricing</summary>
            <div className="mt-3 space-y-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Pest Type</Label>
                  <select value={autoPest.pest_slug} onChange={(e) => setAutoPest({ ...autoPest, pest_slug: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="inv-auto-pest">
                    <option value="">— pick —</option>
                    {pestList.filter(p => p.active).map(p => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Property Size</Label>
                  <select value={autoPest.property_size} onChange={(e) => setAutoPest({ ...autoPest, property_size: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="inv-auto-bhk">
                    <option value="">flat/per-sqft</option>
                    <option value="1bhk">1 BHK</option><option value="2bhk">2 BHK</option><option value="3bhk">3 BHK</option><option value="4bhk">4 BHK</option><option value="villa">Villa</option>
                  </select>
                </div>
                <div><Label className="text-xs">Area (sq.ft)</Label><Input type="number" min="0" value={autoPest.area_sqft} onChange={(e) => setAutoPest({ ...autoPest, area_sqft: e.target.value })} placeholder="if per-sqft" data-testid="inv-auto-sqft" /></div>
                <div><Label className="text-xs">Drilling Points</Label><Input type="number" min="0" value={autoPest.drilling_points} onChange={(e) => setAutoPest({ ...autoPest, drilling_points: e.target.value })} data-testid="inv-auto-drill" /></div>
                <div><Label className="text-xs">Travel (km)</Label><Input type="number" min="0" step="0.5" value={autoPest.travel_km} onChange={(e) => setAutoPest({ ...autoPest, travel_km: e.target.value })} data-testid="inv-auto-travel" /></div>
                <label className="flex items-center gap-2 text-sm self-end pb-1.5">
                  <input type="checkbox" checked={autoPest.emergency} onChange={(e) => setAutoPest({ ...autoPest, emergency: e.target.checked })} data-testid="inv-auto-emerg" />
                  <span>Emergency</span>
                </label>
              </div>
              <Button type="button" onClick={runAutoFill} variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100" data-testid="inv-auto-fill"><RefreshCw size={13} className="mr-1.5" /> Auto-fill line items</Button>
            </div>
          </details>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-base font-bold">🧾 Service Items / Pest Rows</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="inv-form-add-item"><Plus size={13} className="mr-1" /> Add Service Row</Button>
            </div>
            <p className="text-xs text-slate-500 mb-2">Each row = one pest treatment. Click ▶ to expand and add segment, BHK/area, warranty period & details.</p>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {(form.items || []).map((it, i) => {
                const isOpen = expandedRow === i;
                const pestOptions = pestList.filter(p => p.active).map(p => p.name);
                return (
                  <div key={it._row_id || `legacy-row-${i}`} className="border-t first:border-t-0 border-slate-100" data-testid={`inv-srow-${i}`}>
                    {/* Compact summary row */}
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50/60 hover:bg-slate-50">
                      <button type="button" onClick={() => setExpandedRow(isOpen ? -1 : i)} className="w-6 h-6 inline-flex items-center justify-center rounded hover:bg-slate-200" data-testid={`inv-srow-toggle-${i}`}>
                        <ChevronRight size={14} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      </button>
                      <span className="text-xs font-bold text-slate-500 w-6">{(i + 1).toString().padStart(2, '0')}</span>
                      <Input value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} placeholder="Description" className="h-9 text-sm flex-1 min-w-[120px]" data-testid={`inv-item-desc-${i}`} />
                      <Input type="number" min="0" step="0.5" value={it.quantity} onChange={(e) => setItem(i, 'quantity', Number(e.target.value))} className="h-9 text-sm text-right w-16" data-testid={`inv-item-qty-${i}`} />
                      <Input type="number" min="0" step="0.01" value={it.rate} onChange={(e) => setItem(i, 'rate', Number(e.target.value))} className="h-9 text-sm text-right w-24" data-testid={`inv-item-rate-${i}`} />
                      <span className="text-sm font-semibold w-24 text-right">{fmtMoney((Number(it.quantity || 0) * Number(it.rate || 0)))}</span>
                      <button type="button" onClick={() => delItem(i)} className="text-rose-500 hover:text-rose-700 px-1" data-testid={`inv-srow-del-${i}`}><X size={14} /></button>
                    </div>

                    {/* Expanded details — Pest multi-select, Segment, Property Size, Warranty, Description */}
                    {isOpen && (
                      <div className="px-4 py-3 bg-white border-t border-slate-100 space-y-3">
                        {/* Pest selector */}
                        <div>
                          <Label className="text-xs font-semibold">Pest Name (multi-select, &quot;Other&quot; for unlisted)</Label>
                          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`inv-srow-pest-chips-${i}`}>
                            {[...pestOptions, 'Other'].map(name => {
                              const active = (it.pest_names || []).includes(name);
                              return (
                                <button key={name} type="button" onClick={() => toggleItemPest(i, name)}
                                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition ${active ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:border-emerald-400'}`}
                                  data-testid={`inv-srow-pest-${i}-${name.toLowerCase().replace(/\W+/g, '-')}`}
                                >{name}</button>
                              );
                            })}
                          </div>
                          {(it.pest_names || []).includes('Other') && (
                            <Input value={it.pest_other || ''} onChange={(e) => setItem(i, 'pest_other', e.target.value)}
                              placeholder="Type the pest name (e.g., Wasp, Snake, Bird)" className="mt-2 h-9 text-sm" data-testid={`inv-srow-pest-other-${i}`} />
                          )}
                        </div>

                        {/* Segment multi-select */}
                        <div>
                          <Label className="text-xs font-semibold">Property Segment (multi)</Label>
                          <div className="mt-1.5 flex gap-1.5" data-testid={`inv-srow-segs-${i}`}>
                            {[['residential', 'Residential'], ['commercial', 'Commercial'], ['industrial', 'Industrial']].map(([k, label]) => {
                              const on = (it.segments || []).includes(k);
                              return (
                                <button key={k} type="button" onClick={() => toggleItemSegment(i, k)}
                                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold ${on ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                  data-testid={`inv-srow-seg-${i}-${k}`}
                                >{label}</button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Property Size + Area + Warranty */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">Property Size</Label>
                            <select value={it.property_size || ''} onChange={(e) => setItem(i, 'property_size', e.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-300 px-2 text-sm bg-white" data-testid={`inv-srow-size-${i}`}>
                              <option value="">— Choose —</option>
                              <option value="1 BHK">1 BHK</option>
                              <option value="2 BHK">2 BHK</option>
                              <option value="3 BHK">3 BHK</option>
                              <option value="4 BHK">4 BHK</option>
                              <option value="Villa">Villa</option>
                              <option value="Custom">Custom (Area in Sq Ft)</option>
                            </select>
                          </div>
                          {it.property_size === 'Custom' && (
                            <div>
                              <Label className="text-xs">Area (Sq Ft)</Label>
                              <Input type="number" min="0" value={it.area_sqft || ''} onChange={(e) => setItem(i, 'area_sqft', e.target.value)} className="mt-1 h-9 text-sm" data-testid={`inv-srow-area-${i}`} />
                            </div>
                          )}
                          <div>
                            <Label className="text-xs">Warranty Period</Label>
                            <div className="flex gap-1 mt-1">
                              <Input type="number" min="0" value={it.warranty_value || ''} onChange={(e) => setItem(i, 'warranty_value', e.target.value)} placeholder="0" className="h-9 text-sm w-16" data-testid={`inv-srow-warr-val-${i}`} />
                              <select value={it.warranty_unit || 'months'} onChange={(e) => setItem(i, 'warranty_unit', e.target.value)} className="h-9 flex-1 rounded-md border border-slate-300 px-2 text-sm bg-white" data-testid={`inv-srow-warr-unit-${i}`}>
                                <option value="days">Days</option>
                                <option value="months">Months</option>
                                <option value="years">Years</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Detail description + AI generator */}
                        <div>
                          <Label className="text-xs">Detailed Description (instructions, chemicals, observations…)</Label>
                          <textarea
                            rows={2}
                            value={it.description || ''}
                            onChange={(e) => setItem(i, 'description', e.target.value)}
                            placeholder="e.g., Gel baiting in kitchen + spray on baseboards. Re-treat if cockroach activity continues after 14 days."
                            className="mt-1 w-full p-2 rounded-md border border-slate-300 text-sm"
                            data-testid={`inv-srow-detail-${i}`}
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const payload = {
                                  pest_names: it.pest_names || [], pest_other: it.pest_other,
                                  segments: it.segments || [], property_size: it.property_size,
                                  area_sqft: it.area_sqft, warranty_value: it.warranty_value,
                                  warranty_unit: it.warranty_unit,
                                };
                                const r = await api('POST', '/admin/invoices/ai/generate-description', payload);
                                if (r.data?.description) setItem(i, 'description', r.data.description);
                              } catch (e) { setErr(apiErr(e, 'AI generation failed')); }
                            }}
                            className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:text-purple-900"
                            data-testid={`inv-srow-ai-${i}`}
                          ><Sparkles size={12} /> AI Generate Description</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Discount (₹)</Label><Input type="number" min="0" step="0.01" value={form.discount} onChange={(e) => setForm({ ...form, discount: Number(e.target.value) })} data-testid="inv-form-discount" /></div>
            <div><Label>GST Rate % <span className="text-[10px] text-slate-400">{!form.is_gst && '(disabled — Non-GST)'}</span></Label><Input type="number" min="0" step="0.5" value={form.gst_rate} disabled={!form.is_gst} onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })} data-testid="inv-form-gst" /></div>
            <div><Label>Due in (days)</Label><Input type="number" min="0" value={form.due_in_days || 7} onChange={(e) => setForm({ ...form, due_in_days: Number(e.target.value) })} data-testid="inv-form-due" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Amount Paid</Label><Input type="number" min="0" step="0.01" value={form.amount_paid || 0} onChange={(e) => setForm({ ...form, amount_paid: Number(e.target.value) })} data-testid="inv-form-paid" /></div>
            <div><Label>Payment Status</Label>
              <select value={form.payment_status || 'pending'} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="inv-form-status">
                {STATUSES.filter(s => s.v).map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </div>
            <div><Label>Payment Method</Label><Input value={form.payment_method || ''} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} placeholder="cash / upi / bank" data-testid="inv-form-payment-method" /></div>
          </div>

          <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>{fmtMoney(totals.sub)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Discount</span><span>– {fmtMoney(totals.disc)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">{form.is_gst ? `GST (${form.gst_rate}%)` : 'GST (waived — Estimate)'}</span><span>{fmtMoney(totals.gst)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Amount Paid</span><span>{fmtMoney(totals.paid)}</span></div>
            <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-1"><span>Total</span><span data-testid="inv-form-total">{fmtMoney(totals.total)}</span></div>
            <div className="flex justify-between font-bold text-rose-700"><span>Balance Due</span><span>{fmtMoney(totals.balance)}</span></div>
          </div>

          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}
        </div>

        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex flex-col sm:flex-row gap-2 justify-between items-center">
          {isDirty && <div className="text-xs text-slate-500">Unsaved changes detected. Close to cancel or Save Changes to persist.</div>}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => {
              if (isDirty && !busy && !window.confirm('You have unsaved changes. Discard and close?')) return;
              onClose();
            }} disabled={busy}>Cancel</Button>
            <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" disabled={busy} data-testid="inv-form-save">
              {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…</> : (isNew ? 'Create Invoice' : 'Save Changes')}
            </Button>
          </div>
        </div>
      </form>
      {showLookup && (
        <BookingLookupModal
          api={api}
          onClose={() => setShowLookup(false)}
          onPick={(b) => { setShowLookup(false); applyPrefill(b); loadBookingById(b.id || b.booking_id); }}
        />
      )}
    </div>
  );
}

// ============================================================
// Booking Lookup Modal — search bookings to auto-fill the invoice form
// Supports time-filter chips (Today/7d/30d/All/Custom) + text search by
// booking code, customer name, phone, email.
// ============================================================
function BookingLookupModal({ api, onClose, onPick }) {
  const [q, setQ] = useState('');
  const [preset, setPreset] = useState('30d');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const today = () => new Date().toISOString().slice(0, 10);
  const minusDays = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

  const resolveRange = useCallback(() => {
    if (preset === 'today') return { date_from: today(), date_to: today() };
    if (preset === '7d') return { date_from: minusDays(7), date_to: today() };
    if (preset === '30d') return { date_from: minusDays(30), date_to: today() };
    if (preset === 'custom') return { date_from: dateFrom || undefined, date_to: dateTo || undefined };
    return {};
  }, [preset, dateFrom, dateTo]);

  const search = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = { ...resolveRange() };
      if (q.trim()) params.q = q.trim();
      const r = await api('GET', '/admin/bookings/lookup', { params });
      setRows(r.data?.items || []);
    } catch (e) { setErr(apiErr(e)); }
    finally { setLoading(false); }
  }, [api, q, resolveRange]);

  useEffect(() => { search(); }, [search]);

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="inv-lookup-modal">
      <div className="bg-white w-full sm:max-w-2xl rounded-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 bg-indigo-700 text-white">
          <div><div className="text-xs opacity-90">Smart Booking Lookup</div><div className="font-bold">Find Customer Booking</div></div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 space-y-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') search(); }} placeholder="Booking code, name, phone, or email…" className="pl-9 h-10" data-testid="inv-lookup-search" autoFocus />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[['today', 'Today'], ['7d', '7 Days'], ['30d', '30 Days'], ['all', 'All Time'], ['custom', 'Custom']].map(([k, lbl]) => (
              <button key={k} onClick={() => setPreset(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${preset === k ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-300 text-slate-700'}`} data-testid={`inv-lookup-preset-${k}`}>{lbl}</button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="flex gap-2">
              <DateRangePicker
                from={dateFrom}
                to={dateTo}
                onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
                placeholder="Pick a date range"
                className="flex-1"
                data-testid="inv-lookup-range"
              />
              <Button onClick={search} className="h-10 bg-indigo-600 hover:bg-indigo-700" data-testid="inv-lookup-apply">Apply</Button>
            </div>
          )}
        </div>
        <div className="overflow-y-auto flex-1">
          {err && <div className="m-3 text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}
          {loading && <div className="py-12 text-center text-slate-400"><Loader2 className="inline animate-spin" size={20} /></div>}
          {!loading && rows.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No matching bookings.</div>}
          {!loading && rows.map((b) => (
            <button key={b.id} type="button" onClick={() => onPick(b)} className="w-full text-left px-5 py-3 border-t border-slate-100 hover:bg-indigo-50 flex items-start gap-3" data-testid={`inv-lookup-pick-${b.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
                  {b.booking_code}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${b.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : b.status === 'cancelled' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{b.status}</span>
                </div>
                <div className="text-xs text-slate-600 mt-0.5">{b.customer_name} · {b.customer_phone}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{b.service_label || b.pest_type || '—'} · {b.scheduled_date || '—'} {b.customer_email && <>· {b.customer_email}</>}</div>
              </div>
              <ChevronRight size={16} className="text-slate-400 mt-1 flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// VIEW / MARK-PAID MODAL
// ============================================================
function InvoiceActionModal({ api, invoice, onClose, onEdit, onChanged, onDownload, onPrint, onEmail, onWA }) {
  const [payOpen, setPayOpen] = useState(false);
  const [amount, setAmount] = useState(invoice.amount_due || 0);
  const [mode, setMode] = useState(invoice.payment_mode || 'cash');
  const [txn, setTxn] = useState(invoice.payment_transaction_id || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submitPay = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api('POST', `/admin/invoices/${invoice.id}/mark-paid`, { amount: Number(amount), payment_mode: mode, payment_transaction_id: txn || null });
      onChanged();
    } catch (e2) { setErr(apiErr(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="inv-action-modal">
      <div className="bg-white w-full sm:max-w-2xl rounded-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 bg-emerald-700 text-white">
          <div>
            <div className="text-xs opacity-90">The Indian Pest Control</div>
            <div className="font-bold">{invoice.invoice_number}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onEdit} className="text-white/90 border-white/30 hover:border-white/50 hover:text-white" data-testid="inv-action-edit"><Edit2 size={14} className="mr-1" /> Edit</Button>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={18} /></button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <StatusBadge status={invoice.payment_status} />
            <div className="text-xs text-slate-500">Issued {fmtDate(invoice.invoice_date)} · Due {fmtDate(invoice.due_date)}</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Customer</div>
              <div className="text-sm font-semibold text-slate-900">{invoice.customer_name}</div>
              <div className="text-xs text-slate-600">{invoice.customer_phone}</div>
              {invoice.customer_email && <div className="text-xs text-slate-600 truncate">{invoice.customer_email}</div>}
              {invoice.customer_address && <div className="text-xs text-slate-500 mt-1">{invoice.customer_address}</div>}
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Service</div>
              <div className="text-sm font-semibold text-slate-900">{invoice.service_name || '—'}</div>
              {invoice.service_date && <div className="text-xs text-slate-600">Performed: {fmtDate(invoice.service_date)}</div>}
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-emerald-700 text-white text-[11px] uppercase tracking-wider">
                <tr><th className="text-left px-3 py-2">Description</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Rate</th><th className="text-right px-3 py-2">Amount</th></tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-800">{it.description}</td>
                    <td className="px-3 py-2 text-right">{it.quantity}</td>
                    <td className="px-3 py-2 text-right">{fmtMoney(it.rate)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtMoney(it.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ml-auto max-w-xs text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{fmtMoney(invoice.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Discount</span><span>– {fmtMoney(invoice.discount)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">GST ({invoice.gst_rate}%)</span><span>{fmtMoney(invoice.gst_amount)}</span></div>
            <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-1"><span>Total</span><span>{fmtMoney(invoice.total_amount)}</span></div>
            <div className="flex justify-between text-emerald-700"><span>Paid</span><span>{fmtMoney(invoice.amount_paid)}</span></div>
            <div className="flex justify-between font-bold text-rose-700"><span>Balance Due</span><span>{fmtMoney(invoice.amount_due)}</span></div>
          </div>

          {!payOpen && invoice.amount_due > 0 && invoice.payment_status !== 'cancelled' && (
            <Button onClick={() => setPayOpen(true)} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="inv-mark-paid-open"><IndianRupee size={14} className="mr-1.5" /> Record Payment</Button>
          )}

          {payOpen && (
            <form onSubmit={submitPay} className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2" data-testid="inv-mark-paid-form">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div><Label className="text-xs">Amount</Label><Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="inv-pay-amount" /></div>
                <div><Label className="text-xs">Mode</Label>
                  <select value={mode} onChange={(e) => setMode(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="inv-pay-mode">
                    <option value="cash">Cash</option><option value="upi">UPI</option><option value="bank">Bank Transfer</option>
                    <option value="card">Card</option><option value="razorpay">Razorpay</option><option value="cheque">Cheque</option>
                  </select>
                </div>
                <div><Label className="text-xs">Txn ID (optional)</Label><Input value={txn} onChange={(e) => setTxn(e.target.value)} data-testid="inv-pay-txn" /></div>
              </div>
              {err && <div className="text-xs text-rose-700">{err}</div>}
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" size="sm" onClick={() => setPayOpen(false)}>Cancel</Button>
                <Button type="submit" size="sm" className="bg-emerald-700 hover:bg-emerald-800" disabled={busy} data-testid="inv-pay-submit">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : 'Save Payment'}
                </Button>
              </div>
            </form>
          )}
        </div>

        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Button variant="outline" size="sm" onClick={onDownload}><Download size={13} className="mr-1" /> PDF</Button>
          <Button variant="outline" size="sm" onClick={onPrint}><Printer size={13} className="mr-1" /> Print</Button>
          <Button variant="outline" size="sm" onClick={onEmail} disabled={!invoice.customer_email}><Mail size={13} className="mr-1" /> Email</Button>
          <Button variant="outline" size="sm" onClick={onWA}><MessageCircle size={13} className="mr-1" /> WhatsApp</Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// REPORTS — Comprehensive Analytics Dashboard
// ============================================================
const STATUS_COLORS = {
  paid: { bg: 'bg-emerald-100', text: 'text-emerald-800', bar: 'bg-emerald-500' },
  pending: { bg: 'bg-amber-100', text: 'text-amber-800', bar: 'bg-amber-500' },
  partial: { bg: 'bg-blue-100', text: 'text-blue-800', bar: 'bg-blue-500' },
  overdue: { bg: 'bg-rose-100', text: 'text-rose-800', bar: 'bg-rose-500' },
  cancelled: { bg: 'bg-slate-100', text: 'text-slate-600', bar: 'bg-slate-400' },
};
const AGING_COLORS = ['bg-emerald-500', 'bg-amber-400', 'bg-orange-500', 'bg-rose-500', 'bg-red-700'];

function ReportsPanel({ api, invoiceType, categoryLabel }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [revenue, setRevenue] = useState(null);
  const [services, setServices] = useState(null);
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tp = invoiceType ? `&invoice_type=${invoiceType}` : '';
      const [r, s, c] = await Promise.all([
        api('GET', `/admin/invoices/reports/revenue?year=${year}${tp}`),
        api('GET', `/admin/invoices/reports/service-wise?year=${year}${tp}`),
        api('GET', `/admin/invoices/reports/comprehensive?year=${year}${tp}`),
      ]);
      setRevenue(r.data);
      setServices(s.data);
      setComp(c.data);
    } finally { setLoading(false); }
  }, [api, year, invoiceType]);

  useEffect(() => { load(); }, [load]);

  const maxRev = Math.max(...(revenue?.monthly || []).map(m => m.revenue || 0), 1);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const totals = comp?.totals || {};
  const maxAging = Math.max(...(comp?.aging_buckets || []).map(b => b.amount), 1);
  const titleLabel = categoryLabel || 'All Invoices';

  return (
    <div className="space-y-5" data-testid="inv-reports">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-900 inline-flex items-center gap-2">
          <TrendingUp size={18} className="text-emerald-700" /> {titleLabel} · Reports
        </h2>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={load} data-testid="inv-report-refresh">
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">Year</Label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white font-medium" data-testid="inv-report-year">
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      {invoiceType && (
        <div className={`text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-2 ${invoiceType === 'gst' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
          <FileText size={13} />
          Showing {invoiceType === 'gst' ? 'GST Tax Invoices' : 'Non-GST Estimates / Bill of Supply'} only
        </div>
      )}

      {loading && <div className="text-center text-slate-400 py-16"><Loader2 className="inline animate-spin" size={22} /> Loading reports…</div>}

      {!loading && revenue && comp && (
        <>
          {/* KPI Cards */}
          <div className={`grid gap-3 ${invoiceType === 'gst' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6' : invoiceType === 'nongst' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5' : 'grid-cols-2 md:grid-cols-4 lg:grid-cols-7'}`}>
            <RptCard label="Total Invoices" value={totals.invoices || 0} sub={!invoiceType ? `${totals.gst_count || 0} GST · ${totals.nongst_count || 0} Non-GST` : null} Icon={FileText} tone="slate" />
            <RptCard label="Revenue" value={fmtMoney(totals.revenue)} Icon={IndianRupee} tone="emerald" />
            <RptCard label="Collected" value={fmtMoney(totals.collected)} Icon={CheckCircle2} tone="blue" />
            <RptCard label="Outstanding" value={fmtMoney(totals.outstanding)} Icon={AlertCircle} tone="rose" />
            {invoiceType !== 'nongst' && <RptCard label="GST Amount" value={fmtMoney(totals.gst_amount)} Icon={Receipt} tone="violet" />}
            <RptCard label="Avg Invoice" value={fmtMoney(totals.avg_invoice)} Icon={TrendingUp} tone="amber" />
            <RptCard label="Collection %" value={`${totals.collection_rate || 0}%`} Icon={CheckCircle2} tone={totals.collection_rate >= 80 ? 'emerald' : totals.collection_rate >= 50 ? 'amber' : 'rose'} />
          </div>

          {/* Monthly Revenue Chart */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="inv-report-monthly-chart">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 text-sm">Monthly Revenue · {year}</h3>
              <div className="flex items-center gap-4 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> Revenue</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-400 inline-block" /> Collected</span>
                {invoiceType !== 'nongst' && <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-violet-400 inline-block" /> GST</span>}
              </div>
            </div>
            <div className="grid grid-cols-6 md:grid-cols-12 gap-2.5 items-end" style={{ height: '200px' }}>
              {revenue.monthly.map((m, i) => {
                const revH = Math.max(3, Math.round(((m.revenue || 0) / maxRev) * 100));
                const paidH = Math.max(0, Math.round(((m.paid || 0) / maxRev) * 100));
                const gstH = Math.max(0, Math.round(((m.gst || 0) / maxRev) * 100));
                const hasData = (m.revenue || 0) > 0;
                return (
                  <div key={i} className="flex flex-col items-center gap-1 h-full group" title={`${months[i]}: Rev ${fmtMoney(m.revenue)} · Paid ${fmtMoney(m.paid)} · GST ${fmtMoney(m.gst)} · ${m.count} inv`}>
                    <div className="flex-1 w-full flex items-end justify-center gap-0.5 relative">
                      <div className="w-1/3 bg-emerald-500 rounded-t transition-all duration-300" style={{ height: `${revH}%`, minHeight: hasData ? '4px' : '2px' }} />
                      <div className="w-1/3 bg-blue-400 rounded-t transition-all duration-300" style={{ height: `${paidH}%`, minHeight: hasData && m.paid ? '4px' : '2px' }} />
                      {invoiceType !== 'nongst' && <div className="w-1/3 bg-violet-400 rounded-t transition-all duration-300" style={{ height: `${gstH}%`, minHeight: hasData && m.gst ? '4px' : '2px' }} />}
                      {hasData && (
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[9px] font-medium px-2 py-1 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                          {fmtMoney(m.revenue)} · {m.count} inv
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 font-semibold">{months[i]}</div>
                    {hasData && <div className="text-[9px] text-slate-400 font-medium">{m.count}</div>}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-4 pt-3 border-t border-slate-100 text-xs">
              <span className="text-slate-500">Year Total: <span className="font-bold text-slate-900">{fmtMoney(revenue.total_revenue)}</span></span>
              <span className="text-slate-500">Collected: <span className="font-bold text-blue-700">{fmtMoney(revenue.total_paid)}</span></span>
              {invoiceType !== 'nongst' && <span className="text-slate-500">GST: <span className="font-bold text-violet-700">{fmtMoney(revenue.total_gst)}</span></span>}
            </div>
          </div>

          {/* Two-column: Payment Status + Outstanding Aging */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Payment Status Distribution */}
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="inv-report-status-dist">
              <h3 className="font-semibold text-slate-900 text-sm mb-4">Payment Status Distribution</h3>
              {(comp.status_breakdown || []).length === 0 && <p className="text-sm text-slate-400 text-center py-8">No invoices in {year}</p>}
              <div className="space-y-3">
                {(comp.status_breakdown || []).map((s, i) => {
                  const pct = totals.invoices ? Math.round((s.count / totals.invoices) * 100) : 0;
                  const colors = STATUS_COLORS[s.status] || STATUS_COLORS.pending;
                  return (
                    <div key={i} data-testid={`inv-status-row-${s.status}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${colors.bg} ${colors.text}`}>{s.status}</span>
                          <span className="text-xs text-slate-500">{s.count} invoices</span>
                        </div>
                        <span className="text-xs font-semibold text-slate-700">{fmtMoney(s.total)}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-500 ${colors.bar}`} style={{ width: `${Math.max(pct, s.count > 0 ? 3 : 0)}%` }} />
                      </div>
                      <div className="flex justify-between mt-0.5 text-[10px] text-slate-400">
                        <span>{pct}% of total</span>
                        <span>Paid: {fmtMoney(s.paid)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Outstanding Aging */}
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="inv-report-aging">
              <h3 className="font-semibold text-slate-900 text-sm mb-4">Outstanding Aging</h3>
              {(comp.aging_buckets || []).every(b => b.amount === 0) && <p className="text-sm text-slate-400 text-center py-8">No outstanding invoices</p>}
              <div className="space-y-3">
                {(comp.aging_buckets || []).map((b, i) => {
                  const pct = maxAging > 0 ? Math.round((b.amount / maxAging) * 100) : 0;
                  return (
                    <div key={i} data-testid={`inv-aging-row-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${AGING_COLORS[i]}`} />
                          <span className="text-xs font-medium text-slate-700">{b.label}</span>
                          <span className="text-[10px] text-slate-400">({b.count})</span>
                        </div>
                        <span className="text-xs font-bold text-slate-800">{fmtMoney(b.amount)}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-500 ${AGING_COLORS[i]}`} style={{ width: `${Math.max(pct, b.amount > 0 ? 4 : 0)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between text-xs">
                <span className="text-slate-500">Total Outstanding</span>
                <span className="font-bold text-rose-700">{fmtMoney(totals.outstanding)}</span>
              </div>
            </div>
          </div>

          {/* Service-wise Revenue */}
          {services && (
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="inv-report-services">
              <h3 className="font-semibold text-slate-900 text-sm mb-3">Service-wise Revenue</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="text-[10px] text-slate-500 uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2.5 pl-1">Service</th>
                      <th className="text-right py-2.5 px-2">Invoices</th>
                      <th className="text-right py-2.5 px-2">Revenue</th>
                      <th className="text-right py-2.5 px-2">Collected</th>
                      {invoiceType !== 'nongst' && <th className="text-right py-2.5 px-2">GST</th>}
                      <th className="text-right py-2.5 px-2">Due</th>
                      <th className="text-right py-2.5 pr-1 w-[120px]">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.items.map((s, i) => {
                      const totalServiceRev = services.items.reduce((sum, x) => sum + (x.revenue || 0), 0);
                      const pct = totalServiceRev ? Math.round((s.revenue / totalServiceRev) * 100) : 0;
                      return (
                        <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/50">
                          <td className="py-2.5 pl-1 font-medium text-slate-900">{s.service}</td>
                          <td className="py-2.5 px-2 text-right text-slate-600">{s.count}</td>
                          <td className="py-2.5 px-2 text-right font-semibold text-slate-900">{fmtMoney(s.revenue)}</td>
                          <td className="py-2.5 px-2 text-right text-emerald-700 font-medium">{fmtMoney(s.paid)}</td>
                          {invoiceType !== 'nongst' && <td className="py-2.5 px-2 text-right text-violet-600">{fmtMoney(s.gst)}</td>}
                          <td className="py-2.5 px-2 text-right text-rose-600 font-medium">{fmtMoney((s.revenue || 0) - (s.paid || 0))}</td>
                          <td className="py-2.5 pr-1 text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-[10px] text-slate-500 w-8 text-right">{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {services.items.length === 0 && <tr><td colSpan={invoiceType !== 'nongst' ? 7 : 6} className="py-10 text-center text-slate-400">No data for {year}</td></tr>}
                  </tbody>
                  {services.items.length > 0 && (
                    <tfoot className="border-t-2 border-slate-200">
                      <tr className="font-bold text-slate-900">
                        <td className="py-2.5 pl-1">Total</td>
                        <td className="py-2.5 px-2 text-right">{services.items.reduce((a, x) => a + x.count, 0)}</td>
                        <td className="py-2.5 px-2 text-right">{fmtMoney(services.items.reduce((a, x) => a + x.revenue, 0))}</td>
                        <td className="py-2.5 px-2 text-right text-emerald-700">{fmtMoney(services.items.reduce((a, x) => a + x.paid, 0))}</td>
                        {invoiceType !== 'nongst' && <td className="py-2.5 px-2 text-right text-violet-600">{fmtMoney(services.items.reduce((a, x) => a + x.gst, 0))}</td>}
                        <td className="py-2.5 px-2 text-right text-rose-600">{fmtMoney(services.items.reduce((a, x) => a + (x.revenue - x.paid), 0))}</td>
                        <td className="py-2.5 pr-1 text-right text-[10px] text-slate-500">100%</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* Top Customers */}
          {(comp.top_customers || []).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="inv-report-top-customers">
              <h3 className="font-semibold text-slate-900 text-sm mb-3">Top Customers by Revenue</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[500px]">
                  <thead className="text-[10px] text-slate-500 uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2.5 pl-1 w-8">#</th>
                      <th className="text-left py-2.5">Customer</th>
                      <th className="text-right py-2.5 px-2">Invoices</th>
                      <th className="text-right py-2.5 px-2">Total</th>
                      <th className="text-right py-2.5 px-2">Paid</th>
                      <th className="text-right py-2.5 pr-1">Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comp.top_customers.map((c, i) => (
                      <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/50">
                        <td className="py-2 pl-1 text-slate-400 text-xs">{i + 1}</td>
                        <td className="py-2">
                          <div className="font-medium text-slate-900">{c.name}</div>
                          <div className="text-[10px] text-slate-400">{c.phone}</div>
                        </td>
                        <td className="py-2 px-2 text-right text-slate-600">{c.count}</td>
                        <td className="py-2 px-2 text-right font-semibold text-slate-900">{fmtMoney(c.total)}</td>
                        <td className="py-2 px-2 text-right text-emerald-700">{fmtMoney(c.paid)}</td>
                        <td className="py-2 pr-1 text-right text-rose-600 font-medium">{fmtMoney(c.due)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* Compact report KPI card */
function RptCard({ label, value, sub, Icon, tone }) {
  const toneMap = {
    slate: 'bg-slate-50 border-slate-200 text-slate-600',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    violet: 'bg-violet-50 border-violet-200 text-violet-700',
  };
  const cls = toneMap[tone] || toneMap.slate;
  return (
    <div className={`rounded-xl border p-3 ${cls}`} data-testid={`report-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={12} className="opacity-60" />}
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-75">{label}</span>
      </div>
      <div className="text-lg font-bold leading-tight">{value}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}
